import { get, isNull, isUndefined, isNaN } from 'lodash-es';

/**
 * Returns calculator monthly payment:
 * - when it's a positive number, returns as is;
 * - when it's a negative number or when it's a null value, returns null;
 * - when it's undefined or zero, falls back to VIN payment by path.
 * @param {object} vinData
 * @param {string} paymentPath
 * @param {number|null|undefined} calculatorMonthlyPayment
 * @returns {number|null}
 */
export function getMonthlyPayment(vinData, paymentPath, calculatorMonthlyPayment) {
  if (calculatorMonthlyPayment < 0 || isNull(calculatorMonthlyPayment)) {
    return null;
  }

  return calculatorMonthlyPayment || get(vinData, paymentPath, null);
}

/**
 * Returns lease calculator monthly payment with VIN data fallback.
 * When the calculator value is zero, negative, null, undefined, or NaN,
 * falls back to the VIN's pre-loaded lease payment value.
 * @param {object} vinData
 * @param {string} paymentPath
 * @param {number|null|undefined} calculatorLeasePayment
 * @returns {number|null}
 */
export function getLeaseMonthlyPayment(vinData, paymentPath, calculatorLeasePayment) {
  if (calculatorLeasePayment > 0) {
    return calculatorLeasePayment;
  }

  return get(vinData, paymentPath, null);
}

/**
 * Returns whether monthly payment values are still loading.
 *
 * Priority:
 * 1) For CapOne prequalified flow, checks selected offer payment.
 * 2) Otherwise checks lease calculator payment when lease mode is enabled.
 * 3) Otherwise checks loan calculator payment.
 *
 * Undefined or NaN means loading; null/number means loaded.
 * @param {number|null|undefined} [calculatorLoanPayment]
 * @param {number|null|undefined} [calculatorLeasePayment]
 * @param {boolean} [showLeasePayments]
 * @param {boolean} [isCapOnePrequalified]
 * @param {number|null|undefined} [payment]
 * @returns {boolean}
 */
export function isMonthlyPaymentUpdating({
  calculatorLoanPayment,
  calculatorLeasePayment,
  showLeasePayments,
  isCapOnePrequalified,
  payment,
}) {
  if (isCapOnePrequalified) {
    return isUndefined(payment) || isNaN(payment);
  }
  return showLeasePayments
    ? isUndefined(calculatorLeasePayment) || isNaN(calculatorLeasePayment)
    : isUndefined(calculatorLoanPayment) || isNaN(calculatorLoanPayment);
}
